import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Car, CostLine, GeschatteKmStand, Meting, Tarief } from '@fleet/shared';
import {
  AFSCHRIJF_MAANDEN,
  BUCKETS,
  TCO_POSTEN,
  afschrijfBasis,
  afschrijving,
  buildTco,
  eindwaardeVan,
  euro,
  isEigenBezit,
  kmPerMaand,
  monthLabel,
  tcoJaren,
  restwaarde,
  totaalPerPost,
  totaalTco,
} from '@fleet/shared';
import { LineChart } from '../../components/charts/LineChart';
import { StackedBars, type Staaf } from '../../components/charts/StackedBars';
import { Dropdown } from '../../components/ui/Dropdown';
import { Eyebrow } from '../../components/ui/Eyebrow';
import { Panel } from '../../components/ui/Panel';
import { SectieKop } from '../../components/ui/SectieKop';

/**
 * The car's dashboard: one year picker on top, the charts underneath.
 *
 * Picking a year puts all twelve of its months on the axis, also the ones that are
 * still empty: without those filler slots the bars of a running year stretch out
 * into a desert. *Alle jaren* rolls the whole lot up to one bar per year. The years
 * come from the data, so only years we can actually account for are on offer.
 *
 * The cost panels follow that choice. The odometer chart does not: it always shows
 * the whole life, because a meter reading cut to a single year says nothing.
 */
export function CarDashboard({
  car,
  costLines,
  tarieven,
  metingen,
  geschat,
}: {
  car: Car;
  costLines: CostLine[];
  tarieven: Tarief[];
  metingen: Meting[];
  geschat: GeschatteKmStand | null;
}) {
  // Leeg = nog niets gekozen; dan valt hij terug op het laatste jaar met data.
  const [keuze, setKeuze] = useState('');

  const eigen = isEigenBezit(car);
  const jaren = useMemo(() => tcoJaren(car, costLines), [car, costLines]);
  const laatsteJaar = jaren.length ? jaren[jaren.length - 1]! : null;
  const jaar = keuze === 'alle' ? null : keuze ? Number(keuze) : laatsteJaar;
  const soort = jaar == null ? 'jaar' : 'maand';

  const reeks = useMemo(
    () => buildTco({ car, regels: costLines, tarieven, soort, jaar: jaar ?? undefined }),
    [car, costLines, tarieven, soort, jaar],
  );
  const totaal = useMemo(() => totaalTco(reeks), [reeks]);
  const perPost = useMemo(() => totaalPerPost(reeks), [reeks]);
  const periodeLabel = jaar == null ? 'alle jaren' : String(jaar);

  const tcoLagen = TCO_POSTEN.filter((p) => (perPost[p.key] ?? 0) > 0);
  // Alle maanden van het jaar blijven staan, ook de nullen: zo zie je in één
  // oogopslag waar de reeks begint en waar niets is geboekt.
  const staaf = (waarden: Record<string, number>, p: (typeof reeks)[number]): Staaf => ({
    key: p.key,
    label: p.label,
    waarden,
  });
  const tcoStaven = reeks.map((p) => staaf(p.posten, p));
  const varStaven = reeks.map((p) => staaf(p.variabelPerBucket, p));
  // Onderhoud cumulatief: de staaf is het totaal tot en met die periode, en de
  // donkere top is wat er in die periode bij kwam. Zo zie je de opbouw én waar de
  // sprongen zitten, zonder een tweede grafiek.
  let opgeteld = 0;
  const onderhoudStaven = reeks.map((p) => {
    const eerder = opgeteld;
    opgeteld += p.posten.onderhoud;
    return staaf({ eerder, deze: p.posten.onderhoud }, p);
  });
  const ONDERHOUD_LAGEN = [
    { key: 'eerder', label: 'Tot dan toe', color: '#DDB4A6' },
    { key: 'deze', label: jaar == null ? 'Dat jaar' : 'Die maand', color: '#B0543B' },
  ];

  const varLagen = BUCKETS.filter((b) => b.key !== 'onderhoud').filter((b) =>
    reeks.some((p) => (p.variabelPerBucket[b.key] ?? 0) > 0),
  );
  const varTotaal = reeks.reduce((s, p) => s + p.posten.variabel, 0);

  const waarde = useMemo(() => waardeVerloop(car), [car]);
  // De kilometerlijn komt uit de gelogde metingen, dus een handmatig ingevoerde
  // stand telt net zo goed mee als een aflezing bij de pomp. Voor de twaalf
  // elektrische auto's is dat de enige bron die er is.
  const km = useMemo(() => kmLijn(metingen), [metingen]);
  const kostenPerKm = km.gereden && totaal > 0 ? totaal / km.gereden : null;

  return (
    <>
      <SectieKop titel="Dashboard">
        <Dropdown
          value={keuze || (laatsteJaar ? String(laatsteJaar) : '')}
          onChange={setKeuze}
          size="sm"
          minWidth={140}
          ariaLabel="Kies een periode"
          options={[
            ...[...jaren].reverse().map((j) => ({ value: String(j), label: String(j) })),
            ...(jaren.length > 1 ? [{ value: 'alle', label: 'Alle jaren' }] : []),
          ]}
        />
      </SectieKop>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 20 }}>
        <Panel>
          <PaneelKop
            eyebrow="Total cost of ownership"
            bedrag={totaal}
            // Niet meer "tegen het huidige tarief": elke maand rekent sinds de
            // Tarief-tabel met het tarief dat ín die maand gold, en dat is precies
            // het cijfer waar deze grafiek voor bestaat.
            sub={`${periodeLabel} · excl. btw · vaste posten tegen het tarief van elke maand zelf`}
            rechts={
              <span
                style={{
                  fontSize: 11.5,
                  color: '#999999',
                  maxWidth: 260,
                  textAlign: 'right',
                  lineHeight: 1.5,
                }}
              >
                Tik of zweef over een staaf voor die periode. Klik een categorie om hem uit de
                stapel te halen.
              </span>
            }
          />
          {reeks.length ? (
            <StackedBars
              lagen={tcoLagen}
              staven={tcoStaven}
              hoogte={210}
              totaalLabel={`${periodeLabel} · totaal`}
            />
          ) : (
            <LineChart lijnen={[]} hoogte={210} leeg />
          )}
        </Panel>

        <Panel>
          <PaneelKop
            eyebrow="Variabele kosten"
            bedrag={varTotaal}
            sub={`${periodeLabel} · de variabele laag uitgesplitst`}
          />
          {varLagen.length ? (
            <StackedBars
              lagen={varLagen}
              staven={varStaven}
              hoogte={170}
              totaalLabel={`${periodeLabel} · totaal`}
            />
          ) : (
            <LineChart lijnen={[]} hoogte={170} leeg />
          )}
        </Panel>

        <Panel>
          <PaneelKop
            eyebrow="Onderhoudskosten"
            bedrag={perPost.onderhoud}
            sub={
              perPost.onderhoud > 0
                ? `${periodeLabel} · cumulatief opgebouwd uit de geboekte onderhoudsfacturen`
                : `${periodeLabel} · nog geen onderhoud geboekt`
            }
          />
          {perPost.onderhoud > 0 ? (
            <StackedBars
              lagen={ONDERHOUD_LAGEN}
              staven={onderhoudStaven}
              hoogte={170}
              legenda={false}
              totaalLabel={`${periodeLabel} · totaal`}
            />
          ) : (
            <LineChart lijnen={[]} hoogte={170} leeg />
          )}
        </Panel>

        {eigen && (
          <Panel>
            <PaneelKop
              eyebrow="Waarde"
              bedrag={restwaarde(car) ?? 0}
              sub={
                waarde
                  ? // Het werkelijke eindbedrag, niet "20%": vier auto's schrijven af
                    // naar een vaste € 4.500 en daar was 20% aantoonbaar onjuist.
                    `boekwaarde nu · lineair naar ${euro(eindwaardeVan(car))} in 60 maanden · ${
                      waarde.afgeschreven
                        ? `volledig afgeschreven sinds ${waarde.eindLabel}`
                        : `volledig afgeschreven in ${waarde.eindLabel}`
                    }`
                  : 'geen aanschafwaarde of aanschafdatum bekend'
              }
            />
            <LineChart
              lijnen={
                waarde
                  ? [
                      {
                        key: 'waarde',
                        label: 'Boekwaarde',
                        color: '#2A6085',
                        punten: waarde.punten,
                        punten_zichtbaar: true,
                        vlak: true,
                      },
                    ]
                  : []
              }
              hoogte={200}
              xLabels={waarde ? waarde.xLabels : []}
              markerX={waarde && !waarde.afgeschreven ? waarde.nuX : undefined}
              markerLabel="nu"
              leeg={!waarde}
              readout
              standaardIndex={waarde?.nuIndex}
            />
          </Panel>
        )}

        <Panel>
          <PaneelKop
            eyebrow="Kilometerstand"
            bedrag={geschat ? geschat.kmStand : 0}
            eenheid="km"
            sub={
              geschat
                ? `hele levensduur · per maand de hoogste stand${
                    km.perMaand
                      ? ` · ${Math.round(km.perMaand).toLocaleString('nl-NL')} km per maand`
                      : ''
                  }${kostenPerKm ? ` · ${euro(kostenPerKm)} per km` : ''}`
                : 'nog geen meting; een laadpaal vraagt geen tellerstand, dus voeg er hierboven een toe'
            }
          />
          <LineChart
            lijnen={
              km.punten.length > 1
                ? [
                    {
                      key: 'km',
                      label: 'Km-stand',
                      color: '#2A6085',
                      punten: km.punten,
                      punten_zichtbaar: true,
                      vlak: true,
                    },
                  ]
                : []
            }
            hoogte={200}
            eenheid="km"
            xLabels={
              km.punten.length > 1
                ? [km.punten[0]!.label ?? '', km.punten[km.punten.length - 1]!.label ?? '']
                : []
            }
            leeg={km.punten.length < 2}
            readout
          />
        </Panel>
      </div>
    </>
  );
}

/** Eyebrow, headline figure and a caption: the panel header used throughout here. */
function PaneelKop({
  eyebrow,
  bedrag,
  sub,
  eenheid = 'euro',
  rechts,
}: {
  eyebrow: string;
  bedrag: number;
  sub: string;
  eenheid?: 'euro' | 'km';
  rechts?: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap',
        marginBottom: 18,
      }}
    >
      <div>
        <Eyebrow>{eyebrow}</Eyebrow>
        <div style={{ fontSize: 26, fontWeight: 900, color: '#2A6085', marginTop: 4 }}>
          {eenheid === 'euro' ? euro(bedrag) : `${Math.round(bedrag).toLocaleString('nl-NL')} km`}
        </div>
        <div style={{ fontSize: 12, color: '#888888', marginTop: 2 }}>{sub}</div>
      </div>
      {rechts}
    </div>
  );
}

/** Eén punt per kwartaal: 21 punten over vijf jaar blijven aan te tikken. */
const WAARDE_STAP = 3;

function isoMaand(idx: number): string {
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`;
}

/**
 * Book value per quarter over the 60-month window, plus where today falls on it.
 *
 * Runs to the end of the window rather than stopping at today, because the question
 * this chart answers is "is it written off yet, and if not, when". The current month
 * gets a point of its own on top of the quarterly grid, so the value the detail row
 * starts on is exactly the `restwaarde` in the header instead of the nearest quarter.
 *
 * Quarterly rather than monthly on purpose: 61 monthly points give hit bands of a
 * few pixels on a phone, and a straight line has nothing extra to say per month.
 */
function waardeVerloop(car: Car) {
  const koop = afschrijfBasis(car);
  if (!koop || !car.aanschafdatum) return null;
  const start = new Date(car.aanschafdatum);
  if (Number.isNaN(start.getTime())) return null;

  const startIdx = start.getFullYear() * 12 + start.getMonth();
  // Uit de domeinlaag, niet hier opnieuw uitgerekend: dit was een derde eigen kopie
  // van de afschrijvingsformule (naast `restwaarde` en `buildTco`) die de vaste 20%
  // aanhield, dus voor de vier auto's met een restwaarde van € 4.500 liep de lijn
  // onder de boekwaarde in de kop van hetzelfde paneel.
  const perMaand = afschrijving(car);

  const nu = new Date();
  const verstreken = nu.getFullYear() * 12 + nu.getMonth() - startIdx;
  // +1 omdat de aanschafmaand meetelt als eerste afgeschreven maand, gelijk aan
  // `restwaarde` en aan de boekhouding.
  const binnen = Math.min(AFSCHRIJF_MAANDEN, Math.max(0, verstreken + 1));

  // De huidige maand krijgt een eigen punt, ook als die niet op een kwartaal valt.
  // Anders wijst de detailregel het dichtstbijzijnde kwartaal aan en wijkt het bedrag
  // af van de boekwaarde in de kop: bij G382XB € 4.840 tegen € 4.661.
  const maanden = new Set<number>();
  for (let m = 0; m <= AFSCHRIJF_MAANDEN; m += WAARDE_STAP) maanden.add(m);
  maanden.add(binnen);
  const oplopend = [...maanden].sort((a, b) => a - b);

  const punten = oplopend.map((m) => ({
    x: m / AFSCHRIJF_MAANDEN,
    y: koop - perMaand * m,
    label: monthLabel(isoMaand(startIdx + m)),
  }));
  return {
    punten,
    xLabels: [punten[0]!.label, punten[punten.length - 1]!.label],
    nuX: binnen / AFSCHRIJF_MAANDEN,
    nuIndex: oplopend.indexOf(binnen),
    afgeschreven: verstreken >= AFSCHRIJF_MAANDEN,
    eindLabel: monthLabel(isoMaand(startIdx + AFSCHRIJF_MAANDEN)),
  };
}

/** `YYYY-MM` naar een maandindex, zodat de tijdas op maanden rekent. */
function maandIndex(maand: string): number {
  const [jaar, mnd] = maand.split('-').map(Number);
  return (jaar ?? 0) * 12 + (mnd ?? 1) - 1;
}

/**
 * Chart points for the odometer series.
 *
 * Which readings count and how they roll up per month is `kmPerMaand` in the
 * shared domain — that used to live here as a third, separate definition, with
 * its own hard-coded ceiling and no koppeling check at all, so the figure on the
 * card and the line underneath it could disagree about the same car.
 *
 * What is left here is placement: the x-position runs on months rather than on
 * the record count, so a gap in the data stays a gap on the axis.
 */
function kmLijn(metingen: readonly Meting[]) {
  const { punten, gereden, perMaand } = kmPerMaand(metingen);
  if (punten.length < 2) return { punten: [], gereden: 0, perMaand: null as number | null };

  const van = maandIndex(punten[0]!.maand);
  const spanne = Math.max(1, maandIndex(punten[punten.length - 1]!.maand) - van);
  return {
    punten: punten.map((p) => ({
      x: (maandIndex(p.maand) - van) / spanne,
      y: p.km,
      label: monthLabel(p.maand),
    })),
    gereden,
    perMaand,
  };
}
